import { useEffect, useMemo, useState } from "react";

import {
  faArrowRight,
  faBracketsCurly,
  faCircleNotch,
  faInfoCircle,
  faPlug,
  faWarning,
} from "@fortawesome/pro-regular-svg-icons";
import { AxiosError } from "axios";
import {
  endOfDay,
  isBefore,
  parseISO,
  startOfDay,
  startOfMinute,
  sub,
} from "date-fns";
import { orderBy } from "lodash-es";
import { m } from "motion/react";

import { DecisionsOutcomeFilter } from "src/api/decisionHistoryV2/decisionHistoryQueries";
import { Flow, FlowVersion } from "src/api/flowTypes";
import { useFlowVersion } from "src/api/flowVersionQueries";
import { useFlow, useWorkspace } from "src/api/queries";
import { DatasetPurpose } from "src/api/types";
import { Button } from "src/base-components/Button";
import {
  DateRange,
  DateRangePicker,
} from "src/base-components/DateRangePicker";
import { FormItem } from "src/base-components/FormItem";
import { Input } from "src/base-components/Input";
import { Pill } from "src/base-components/Pill";
import { SELECT_DIVIDER, Select } from "src/base-components/Select";
import { SimpleRadioGroup } from "src/base-components/SimpleRadioGroup";
import { Switch } from "src/base-components/Switch";
import { FlowVersionStatus, TrafficPolicyInDB } from "src/clients/flow-api";
import {
  DownloadButton,
  DownloadDetails,
} from "src/datasets/AssembleDatasetModal/DownloadButton";
import type { DateRangeMode } from "src/datasets/AssembleDatasetModal/dateValidation";
import {
  isSimulationDateBeforeAvailableData,
  isStartDateBeforeAvailableData,
  TEST_DATASET_START_DATE,
} from "src/datasets/AssembleDatasetModal/dateValidation";
import { EmptyState } from "src/datasets/AssembleDatasetModal/EmptyState";
import { OutcomesFilter } from "src/datasets/AssembleDatasetModal/OutcomesFilter";
import { AssembleDataTable } from "src/datasets/AssembleDatasetModal/Table";
import { TableColumns } from "src/datasets/AssembleDatasetModal/TableColumns";
import { TableSkeleton } from "src/datasets/AssembleDatasetModal/TableSkeleton";
import {
  useCreateAssembleDatasetJob,
  useHistoryData,
} from "src/datasets/api/queries";
import { useAvailableIntegrationNodes } from "src/datasets/DatasetTable/hooks";
import { Accordion } from "src/design-system/Accordion";
import { EmptyState as CommonEmptyState } from "src/design-system/EmptyState";
import { ErrorHint } from "src/design-system/ErrorHint";
import { Icon } from "src/design-system/Icon";
import { Modal } from "src/design-system/Modal";
import { toastActions } from "src/design-system/Toast/utils";
import { Tooltip } from "src/design-system/Tooltip";
import { POSSIBLE_DECISION_STATUS_CODES } from "src/flow/decisionHistory/SharedStatusColumn";
import { useIsIcebergDatasetGenerationEnabled } from "src/hooks/useIsIcebergDatasetGenerationEnabled";
import { SchemaOptions } from "src/router/SearchParams";
import { AuthorPageParams } from "src/router/urls";
import { SchemaConverter } from "src/schema/schemaMappingUtils";
import { useSimulationAccess } from "src/simulations/useSimulationAccess";
import { formatDate } from "src/utils/datetime";
import { useParamsDecode } from "src/utils/useParamsDecode";

/**
 * When initialized with a traffic policy id as prop, it automatically
 * sets the date range mode to "traffic_policy"
 */
type Props = {
  open: boolean;
  onClose: () => void;
  flow: Flow;
  trafficPolicies: TrafficPolicyInDB[];
  purpose?: DatasetPurpose;
  trafficPolicyIdDefaultOverride?: string;
};

// We don't allow pending decisions to be added to datasets
const CREATE_DATASET_DECISION_CODES = POSSIBLE_DECISION_STATUS_CODES.filter(
  (entry) => entry.key !== "202",
);

const EXPORT_LIMIT = 100_000;
const SIMULATION_DATASET_DECISION_LIMIT = 1_000_000_000;
const SAMPLE_SIZE_DEFAULT = 10_000;

const dateRangeCanBeTimeWindow = (
  dateRange: DateRange | undefined,
): dateRange is { from: Date; to: Date } => {
  return !!dateRange && !!dateRange.from && !!dateRange.to;
};

const dateRangeToTimeWindow = (dateRange: {
  from: Date;
  to: Date;
}): [string, string] => {
  const startDate = startOfDay(dateRange.from);
  const endDate = endOfDay(dateRange.to);

  return [startDate.toISOString(), endDate.toISOString()];
};

const getTimeWindowForTrafficPolicy = (
  trafficPolicy: TrafficPolicyInDB,
): [string, string] => {
  // Fallback in case the policy has no time windows
  if (
    !trafficPolicy.traffic_policy_windows ||
    trafficPolicy.traffic_policy_windows.length === 0
  ) {
    return [trafficPolicy.created_at, startOfMinute(new Date()).toISOString()];
  }
  const timeWindowsSorted = trafficPolicy.traffic_policy_windows
    .slice()
    .sort(
      (a, b) =>
        new Date(a.created_at).valueOf() - new Date(b.created_at).valueOf(),
    );

  /* The time window of a policy is a date range between
     the start date of its earliest time window and the end
     date of its latest time window. If the latest time window
     is still active, then it sets the current date.
  */
  const startOfFirstWindow = timeWindowsSorted[0].starts_at;
  const endOfLastWindow =
    timeWindowsSorted.at(-1)?.ends_at ??
    startOfMinute(new Date()).toISOString();

  return [startOfFirstWindow, endOfLastWindow];
};

const getReadableTrafficPolicyTimeWindow = (
  trafficPolicy: TrafficPolicyInDB,
) => {
  if (
    !trafficPolicy.traffic_policy_windows ||
    trafficPolicy.traffic_policy_windows.length === 0
  ) {
    return null;
  }

  const timeWindowsSorted = trafficPolicy.traffic_policy_windows
    .slice()
    .sort(
      (a, b) =>
        new Date(a.created_at).valueOf() - new Date(b.created_at).valueOf(),
    );

  const start = formatDate(timeWindowsSorted[0].starts_at, "MMMM d");

  const lastWindow = timeWindowsSorted.at(-1);
  const end = lastWindow?.ends_at
    ? formatDate(lastWindow.ends_at, "MMMM d")
    : "present";

  return `(${start} - ${end})`;
};

const useAssembleDatasetModalQuery = ({
  statusCodes,
  flowVersionIds,
  flowSlug,
  timeWindow,
  trafficPolicyId,
  subSamplingSize,
  outcomeFilters,
}: {
  statusCodes: string[];
  flowVersionIds: string[];
  flowSlug: string;
  timeWindow: [string, string];
  trafficPolicyId?: string;
  subSamplingSize?: number;
  outcomeFilters?: DecisionsOutcomeFilter[];
}) => {
  const { wsId } = useParamsDecode<AuthorPageParams>();
  const workspace = useWorkspace(wsId);

  return useHistoryData({
    flowVersionIds,
    timeWindow,
    statusCodes,
    baseUrl: workspace.data?.base_url ?? "",
    flowSlug,
    trafficPolicyId,
    subSamplingSize,
    outcomeFilters,
  });
};

const useCreateDatasetFromHistoryMutation = () => {
  const { wsId, version_id } = useParamsDecode<AuthorPageParams>();
  const workspace = useWorkspace(wsId);
  const version = useFlowVersion(version_id);
  const isIcebergDatasetGenEnabled = useIsIcebergDatasetGenerationEnabled();

  return useCreateAssembleDatasetJob(
    workspace.data?.base_url,
    version.data,
    isIcebergDatasetGenEnabled,
  );
};

export const AssembleDatasetModal: React.FC<Props> = (props) => {
  const [showBody, setShowBody] = useState(props.open);

  // Reset the whole body state
  // and avoid flickering during transitions
  useEffect(() => {
    if (props.open) {
      setShowBody(true);
    }
  }, [props.open]);

  return (
    <Modal
      afterLeave={() => setShowBody(false)}
      open={props.open}
      size="lg"
      onClose={props.onClose}
    >
      {showBody && <AssembleDatasetModalBody {...props} />}
    </Modal>
  );
};

const AssembleDatasetModalBody: React.FC<Props> = ({
  onClose,
  flow,
  trafficPolicies,
  trafficPolicyIdDefaultOverride,
  purpose = "test_run",
}) => {
  const [mockNodeNamesToInclude, setMockNodeNamesToInclude] = useState<
    string[]
  >([]);
  const [outputColumnNamesToInclude, setOutputColumnNamesToInclude] = useState<
    string[]
  >([]);
  const { version_id } = useParamsDecode<AuthorPageParams>();
  const version = useFlowVersion(version_id);
  const mockableNodes = useAvailableIntegrationNodes(version.data);
  const isIcebergDatasetGenEnabled = useIsIcebergDatasetGenerationEnabled();

  const { data: flowWithArchived } = useFlow(flow.id, {
    includeArchivedFlowVersions: true,
  });
  const flowVersions = flowWithArchived?.versions ?? flow.versions;

  const flowPublishedAndArchivedVersions = flowVersions.filter(
    (version) =>
      version.status === FlowVersionStatus.PUBLISHED ||
      version.status === FlowVersionStatus.ARCHIVED,
  );

  const flowPublishedAndArchivedVersionOrdered = orderBy(
    flowPublishedAndArchivedVersions,
    ["status", (v) => new Date(v.created_at).valueOf()],
    ["desc", "asc"],
  );

  const { version_id: currentVersionId } = useParamsDecode<AuthorPageParams>();
  const currentVersionIsPublishedOrArchived =
    flowPublishedAndArchivedVersionOrdered.some(
      (v) => v.id === currentVersionId,
    );

  const flowNewestVersion = flowPublishedAndArchivedVersionOrdered.at(-1);

  const [selectedVersions, setSelectedVersions] = useState<string[]>(() => {
    if (currentVersionIsPublishedOrArchived) {
      return [currentVersionId];
    }

    if (flowNewestVersion) {
      return [flowNewestVersion.id];
    }

    return [];
  });

  const simulationAccess = useSimulationAccess();
  // Get the DH V2 date from workspace config
  const getDhV2AvailableAfterDate = (): Date | undefined => {
    if (!simulationAccess.configDate) {
      return undefined;
    }
    return new Date(simulationAccess.configDate);
  };

  const getDataAvailabilityWarning = (purpose: DatasetPurpose): string => {
    if (purpose === "simulation") {
      const configDateOnly = simulationAccess.configDate;

      if (!configDateOnly) {
        return "Simulation datasets require workspace configuration"; // Fallback message
      }
      const availableDate = parseISO(configDateOnly);
      const displayDate = sub(availableDate, { days: -1 });
      return `Data is available from ${formatDate(
        displayDate,
        "d MMM yyyy",
      )} onwards for simulation datasets`;
    }
    return `Data is available from ${formatDate(
      TEST_DATASET_START_DATE,
      "d MMM yyyy",
    )} onwards`;
  };

  /**
   * Date range (picker value) and time window (what's actually used)
   * are two separate states because date range can be in an intermediate
   * or undefined state (i.e. only the start is selected) and we want the
   * table to still use the last value until a convertable date range picker
   * value is selected again
   */
  const dhv2Date = getDhV2AvailableAfterDate();
  const dhv2MinDate = dhv2Date ? sub(dhv2Date, { days: -1 }) : undefined;

  const defaultFrom = (() => {
    const yesterday = sub(new Date(), { days: 1 });
    if (
      purpose === "simulation" &&
      dhv2MinDate &&
      isBefore(yesterday, dhv2MinDate)
    ) {
      return dhv2MinDate;
    }
    return yesterday;
  })();

  const today = new Date();
  const DEFAULT_DATERANGE = {
    from: defaultFrom,
    to: isBefore(today, defaultFrom) ? defaultFrom : today,
  };
  const [dateRange, setDateRange] = useState<DateRange | undefined>(
    DEFAULT_DATERANGE,
  );
  const [timeWindow, setTimeWindow] = useState<[string, string]>(
    dateRangeToTimeWindow(DEFAULT_DATERANGE),
  );

  const [statusCodes, setStatusCodes] = useState<string[]>(
    CREATE_DATASET_DECISION_CODES.map((code) => code.key),
  );
  const [name, setName] = useState("");

  const [dateRangeMode, setDateRangeMode] = useState<DateRangeMode>(
    trafficPolicyIdDefaultOverride ? "traffic_policy" : "time_window",
  );

  const [enableRandomSampling, setEnableRandomSampling] = useState(false);
  const [sampleSize, setSampleSize] = useState(SAMPLE_SIZE_DEFAULT);

  /**
   * The default traffic policy selected can be, in this order of priority:
   * - One policy that was passed as prop
   * - The first policy of the flow
   */
  const defaultTrafficPolicyId =
    trafficPolicyIdDefaultOverride ?? trafficPolicies.at(0)?.id;

  const [selectedTrafficPolicyId, setSelectedTrafficPolicyId] = useState(
    defaultTrafficPolicyId,
  );

  const selectedTrafficPolicy = trafficPolicies.find(
    (p) => p.id === selectedTrafficPolicyId,
  );

  // Select the current traffic policy's versions
  // if the user tries to filter by that
  useEffect(() => {
    if (selectedTrafficPolicy?.policy && dateRangeMode === "traffic_policy") {
      const trafficPolicyVersions =
        selectedTrafficPolicy.policy.flow_versions.map(
          (version) => version.flow_version_id,
        );
      setSelectedVersions(trafficPolicyVersions);
    }
  }, [dateRangeMode, selectedTrafficPolicy]);

  const [outcomesFilter, setOutcomesFilter] =
    useState<Nullable<DecisionsOutcomeFilter>>(null);

  const result = useAssembleDatasetModalQuery(
    dateRangeMode === "time_window" || !selectedTrafficPolicy
      ? {
          timeWindow,
          flowSlug: flow.slug,
          flowVersionIds: selectedVersions,
          statusCodes,
          subSamplingSize: enableRandomSampling ? sampleSize : undefined,
          outcomeFilters: outcomesFilter ? [outcomesFilter] : undefined,
        }
      : {
          trafficPolicyId: selectedTrafficPolicy.id,
          flowSlug: flow.slug,
          flowVersionIds: selectedVersions,
          statusCodes,
          timeWindow: getTimeWindowForTrafficPolicy(selectedTrafficPolicy),
          subSamplingSize: enableRandomSampling ? sampleSize : undefined,
          outcomeFilters: outcomesFilter ? [outcomesFilter] : undefined,
        },
  );

  const createDatasetMutation = useCreateDatasetFromHistoryMutation();

  // Calculate the appropriate estimated row count based on dataset purpose
  const estimatedTotalRowCount =
    purpose === "simulation" && result.data
      ? result.data.total_count
      : undefined;

  const handleOnSubmit = async () => {
    if (result.data) {
      try {
        if (dateRangeMode === "time_window" || !selectedTrafficPolicy) {
          await createDatasetMutation.mutateAsync({
            purpose: purpose,
            flow_id: flow.id,
            flow_slug: flow.slug,
            name,
            filters: {
              start_date: timeWindow[0],
              end_date: timeWindow[1],
              flow_versions: selectedVersions,
              status_codes: statusCodes,
              outcome_filters: outcomesFilter ? [outcomesFilter] : undefined,
            },
            sub_sampling_size: enableRandomSampling ? sampleSize : undefined,
            mock_node_names_to_include:
              mockNodeNamesToInclude.length > 0
                ? mockNodeNamesToInclude
                : undefined,
            output_column_names_to_include:
              outputColumnNamesToInclude.length > 0
                ? outputColumnNamesToInclude
                : undefined,
            estimated_total_row_count: estimatedTotalRowCount,
          });
        } else {
          const timeWindowForTrafficPolicy = getTimeWindowForTrafficPolicy(
            selectedTrafficPolicy,
          );
          await createDatasetMutation.mutateAsync({
            purpose: purpose,
            flow_id: flow.id,
            flow_slug: flow.slug,
            name,
            filters: {
              start_date: timeWindowForTrafficPolicy[0],
              end_date: timeWindowForTrafficPolicy[1],
              flow_versions: selectedVersions,
              status_codes: statusCodes,
              traffic_policy_id: selectedTrafficPolicy.id,
              outcome_filters: outcomesFilter ? [outcomesFilter] : undefined,
            },
            sub_sampling_size: enableRandomSampling ? sampleSize : undefined,
            mock_node_names_to_include: mockNodeNamesToInclude,
            output_column_names_to_include: outputColumnNamesToInclude,
            estimated_total_row_count: estimatedTotalRowCount,
          });
        }
        toastActions.loading({
          title: "Dataset creation started",
          duration: 4000,
        });
        onClose();
      } catch {
        toastActions.failure({
          title: "Something went wrong while trying to save the dataset",
        });
      }
    }
  };

  const queryIsEnabled = !!selectedVersions.length;

  const numberFormatter = new Intl.NumberFormat(undefined, {
    compactDisplay: "short",
    notation: "compact",
  });

  const numberOfDecisionsFormatted =
    (purpose === "simulation" && result.data
      ? numberFormatter.format(result.data.total_count)
      : 0) ||
    (result.data
      ? numberFormatter.format(Math.min(result.data.total_count, EXPORT_LIMIT))
      : 0);

  const datasetLimit =
    purpose === "simulation" ? SIMULATION_DATASET_DECISION_LIMIT : EXPORT_LIMIT;
  const exportLimitFormatted = new Intl.NumberFormat().format(datasetLimit);

  const numberOfDecisionsExceedExportLimit =
    !!result.data &&
    (result.data.total_count > datasetLimit ||
      (result.data.total_count === datasetLimit && result.data.limit_reached));

  const numberOfDecisionsExceedLimit =
    (purpose === "simulation" && !!result.data && result.data.limit_reached) ||
    numberOfDecisionsExceedExportLimit;

  const datasetLimitExceeded =
    purpose === "test_run" &&
    numberOfDecisionsExceedLimit &&
    !enableRandomSampling;

  const wrapWithSubSamplingTooltip = (children: React.ReactNode) => {
    return (
      <Tooltip
        body={`Please modify your filters to stay within this limit or generate a random sub-sample within this limit. The maximum number of decisions allowed in a single dataset is ${exportLimitFormatted} decisions.`}
        disabled={!datasetLimitExceeded}
        footerAction={{
          text: "Enable random sampling",
          onClick: () => setEnableRandomSampling(true),
        }}
        placement="top"
        title="Dataset limit exceeded"
        asChild
      >
        <div className="cursor-default">{children}</div>
      </Tooltip>
    );
  };

  const downloadAvailable =
    result.data && result.data.total_count > 0 && !datasetLimitExceeded;

  const startDateBefore = useMemo(
    () => isStartDateBeforeAvailableData(timeWindow[0], purpose, dhv2MinDate),
    [timeWindow, purpose, dhv2MinDate],
  );

  const trafficPolicyBefore = useMemo(() => {
    if (
      purpose !== "simulation" ||
      dateRangeMode !== "traffic_policy" ||
      !selectedTrafficPolicy ||
      !dhv2MinDate
    ) {
      return false;
    }
    const [policyStart] = getTimeWindowForTrafficPolicy(selectedTrafficPolicy);
    return isBefore(new Date(policyStart), dhv2MinDate);
  }, [purpose, dateRangeMode, selectedTrafficPolicy, dhv2MinDate]);

  const simulationDateBeforeAvailableData = isSimulationDateBeforeAvailableData(
    purpose,
    dateRangeMode,
    startDateBefore,
    trafficPolicyBefore,
  );

  const submitAvailable =
    downloadAvailable && name && !simulationDateBeforeAvailableData;

  const [sampleSizeError, setSampleSizeError] = useState<string | undefined>(
    undefined,
  );

  const [enableExpectedOutputs, setEnableExpectedOutputs] = useState(false);

  const [enableMockData, setEnableMockData] = useState(false);

  const title =
    purpose === "simulation"
      ? "Simulation dataset from historical decisions"
      : "Test dataset from historical decisions";

  const subSamplingSettings = (
    <>
      <div className="flex justify-between">
        <FormItem
          description="Randomly select a subset of historical decisions"
          gap={false}
          label="Random sub-sampling"
        />
        <Switch
          enabled={enableRandomSampling}
          onChange={setEnableRandomSampling}
        />
      </div>
      {enableRandomSampling && (
        <m.div
          animate={{ opacity: 1 }}
          className="rounded-lg bg-gray-50 px-3 py-4"
          initial={{ opacity: 0 }}
        >
          <FormItem
            description={`Maximum sample size is ${exportLimitFormatted} decisions.`}
            gap="sm"
            label="Sample size"
            isRequired
          >
            <div className="relative">
              <div className="pointer-events-none absolute top-[1px] right-[1px] bottom-[1px] flex items-center justify-center rounded-r-lg border-gray-200 border-l bg-gray-100 px-2 font-inter-normal-12px text-gray-500">
                decisions
              </div>
              <Input
                defaultValue={SAMPLE_SIZE_DEFAULT}
                errored={!!sampleSizeError}
                type="number"
                fullWidth
                onChange={(e) => {
                  const parsedValue = e.target.valueAsNumber;
                  if (isNaN(parsedValue)) {
                    setSampleSizeError("Invalid number");
                    return;
                  }
                  if (parsedValue > datasetLimit) {
                    setSampleSizeError(
                      `Maximum sample size is ${exportLimitFormatted} decisions`,
                    );
                    return;
                  }
                  if (parsedValue < 1) {
                    setSampleSizeError(`Minimum sample size is 1 decision`);
                    return;
                  }
                  setSampleSize(parsedValue);
                  setSampleSizeError(undefined);
                }}
              />
            </div>
            {sampleSizeError && <ErrorHint>{sampleSizeError}</ErrorHint>}
          </FormItem>
        </m.div>
      )}
    </>
  );

  const limitMockDataSettings = (
    <>
      <div className="flex justify-between">
        <FormItem
          description={
            <>
              Select which nodes to mock in your Test Dataset. Selecting a
              Decision Flow node includes mock data for both its overall output
              and <span className="underline">not</span> individual external
              responses within the Child Flow
            </>
          }
          gap={false}
          label={
            <div className="flex items-center gap-x-1">
              Limit mock data
              <Tooltip
                body="Select which integrations should have their historical data included as mock data in the Test Dataset. This data is used in test runs to reproduce the nodes’ historical behaviour. Removing unneeded mock data will make your Test Datasets smaller and may allow you to assemble more historical decisions. Not including mock data may cause issues with test running. Mock data for Loop nodes is always included."
                placement="top"
                title="Limit mock data"
              >
                <span className="flex">
                  <Icon icon={faInfoCircle} size="xs" />
                </span>
              </Tooltip>
            </div>
          }
        />
        <Switch enabled={enableMockData} onChange={setEnableMockData} />
      </div>
      {enableMockData && (
        <m.div animate={{ opacity: 1 }} initial={{ opacity: 0 }}>
          <Select
            dropdownPlaceholder={
              <CommonEmptyState
                description="This Decision Flow does not contain any external connections."
                headline="No connection nodes"
                icon={faPlug}
                size="compact"
              />
            }
            options={mockableNodes.map((node) => ({
              key: node.name,
              value: node.name,
            }))}
            placeholder="Select mock node columns..."
            value={mockNodeNamesToInclude}
            multiple
            onChange={setMockNodeNamesToInclude}
          />
        </m.div>
      )}
    </>
  );

  const limitExpectedOutputsSettings = (
    <>
      {version.data?.output_schema &&
        Object.keys(version.data.output_schema).length > 0 && (
          <>
            <div className="flex justify-between">
              <FormItem
                description="Select which Outputs to include as Expected Output in your Test Dataset"
                gap={false}
                label={
                  <div className="flex items-center gap-x-1">
                    Limit Expected Outputs
                    <Tooltip
                      body="Select which Flow Outputs to include as Expected Outputs in your Test Dataset. Expected Outputs let you compare a Flow’s Output against the historical Outputs to see how a Flow’s behaviour has changed in a Test Run. Removing unneeded Expected Outputs will make your Test Datasets smaller and may allow you to assemble more historical decisions."
                      placement="top"
                      title="Limit Expected Outputs"
                    >
                      <span className="flex">
                        <Icon icon={faInfoCircle} size="xs" />
                      </span>
                    </Tooltip>
                  </div>
                }
              />
              <Switch
                enabled={enableExpectedOutputs}
                onChange={setEnableExpectedOutputs}
              />
            </div>
            {enableExpectedOutputs && (
              <m.div animate={{ opacity: 1 }} initial={{ opacity: 0 }}>
                <Select
                  dropdownPlaceholder={
                    <CommonEmptyState
                      description="This Decision Flow does not have an output schema. No Expected Outcomes can be added to this Test Dataset."
                      headline="No output schema"
                      icon={faBracketsCurly}
                      size="compact"
                    />
                  }
                  options={SchemaConverter.beToUI(
                    version.data.output_schema,
                    SchemaOptions.Output,
                  ).properties.map((property) => ({
                    key: property.fieldName,
                    value: property.fieldName,
                  }))}
                  placeholder="Select expected output columns..."
                  value={outputColumnNamesToInclude}
                  multiple
                  onChange={setOutputColumnNamesToInclude}
                />
              </m.div>
            )}
          </>
        )}
    </>
  );
  return (
    <>
      <Modal.Header>
        <div className="flex items-center">
          {title}
          {queryIsEnabled && (
            <div className="ml-2.5 flex items-center">
              {(() => {
                if (result.isLoading) {
                  return (
                    <Pill size="sm">
                      <Pill.Text>
                        <Icon
                          icon={faCircleNotch}
                          size="xs"
                          variant="active"
                          spin
                        />
                      </Pill.Text>
                    </Pill>
                  );
                }

                if (datasetLimitExceeded) {
                  return wrapWithSubSamplingTooltip(
                    <Pill size="sm" variant="red">
                      <Pill.Text>{`${numberOfDecisionsFormatted}+ decisions`}</Pill.Text>
                      <Pill.Icon icon={faWarning} />
                    </Pill>,
                  );
                }

                return result.data ? (
                  <Pill size="sm">
                    <Pill.Text>{`${numberOfDecisionsFormatted}${
                      numberOfDecisionsExceedLimit ? "+" : ""
                    } decisions`}</Pill.Text>
                  </Pill>
                ) : (
                  <Pill size="sm" variant="red">
                    <Pill.Text>Error</Pill.Text>
                  </Pill>
                );
              })()}
              {enableRandomSampling && (
                <>
                  <m.div
                    animate={{ opacity: 1 }}
                    className="mx-1"
                    initial={{ opacity: 0 }}
                  >
                    <Icon icon={faArrowRight} size="3xs" />
                  </m.div>
                  <m.div
                    animate={{ opacity: 1 }}
                    initial={{ opacity: 0 }}
                    transition={{ delay: 0.3 }}
                  >
                    <Pill size="sm">
                      <Pill.Text>{`${numberFormatter.format(
                        sampleSize,
                      )} random decisions`}</Pill.Text>
                    </Pill>
                  </m.div>
                </>
              )}
            </div>
          )}
        </div>
      </Modal.Header>
      <Modal.Content noScroll>
        <div className="flex min-h-0 flex-1">
          <div className="decideScrollbar flex w-[424px] flex-col gap-y-5 overflow-auto border-gray-200 border-r py-6 pr-2">
            <FormItem gap={false} label="Name this dataset" isRequired>
              <Input
                data-loc="assemble-dataset-name-input"
                placeholder="e.g. Jan_March_2023"
                value={name}
                fullWidth
                onChange={(e) => setName(e.target.value)}
              />
            </FormItem>
            <FormItem gap={false} label="Decision Flow version" isRequired>
              {flowPublishedAndArchivedVersions.length ? (
                <VersionsSelect
                  value={selectedVersions}
                  versions={flowPublishedAndArchivedVersionOrdered}
                  onChange={setSelectedVersions}
                />
              ) : (
                <p className="h-10 font-inter-normal-12px text-gray-500">
                  No published or archived versions available for this Flow
                </p>
              )}
            </FormItem>
            <div>
              <SimpleRadioGroup
                className="mb-2 gap-x-13"
                orientation="horizontal"
                value={dateRangeMode}
                onValueChange={(value) =>
                  setDateRangeMode(value as "time_window" | "traffic_policy")
                }
              >
                <SimpleRadioGroup.Item
                  label="Date range"
                  labelClassName="font-inter-normal-12px ml-2.5"
                  value="time_window"
                  boldLabel
                />
                <SimpleRadioGroup.Item
                  label="Routing policy"
                  labelClassName="font-inter-normal-12px ml-2.5"
                  value="traffic_policy"
                  boldLabel
                />
              </SimpleRadioGroup>
              {dateRangeMode === "time_window" && (
                <>
                  {(purpose !== "simulation" || dhv2Date) && (
                    <>
                      <DateRangePicker
                        disabledBefore={
                          purpose === "simulation" ? dhv2MinDate : undefined
                        }
                        value={dateRange}
                        onChange={(range) => {
                          setDateRange(range);
                          if (dateRangeCanBeTimeWindow(range)) {
                            setTimeWindow(dateRangeToTimeWindow(range));
                          }
                        }}
                      />
                      {startDateBefore && (
                        <p
                          className="mt-1 font-inter-normal-12px text-gray-500"
                          data-loc="data-availability-warning"
                        >
                          {getDataAvailabilityWarning(purpose)}
                        </p>
                      )}
                    </>
                  )}
                </>
              )}
              {dateRangeMode === "traffic_policy" &&
                (trafficPolicies.length ? (
                  <>
                    <Select
                      dataLoc="assemble-dataset-traffic-policy-select"
                      options={trafficPolicies.map((policy) => ({
                        key: policy.id,
                        value: (
                          <span>
                            {policy.name}
                            <span className="ml-1.5 text-gray-400">
                              {getReadableTrafficPolicyTimeWindow(policy)}
                            </span>
                          </span>
                        ),
                      }))}
                      value={selectedTrafficPolicyId}
                      onChange={setSelectedTrafficPolicyId}
                    />
                    {trafficPolicyBefore && (
                      <p
                        className="mt-1 font-inter-normal-12px text-gray-500"
                        data-loc="data-availability-warning"
                      >
                        {getDataAvailabilityWarning(purpose)}
                      </p>
                    )}
                  </>
                ) : (
                  <p className="h-10 font-inter-normal-12px text-gray-500">
                    No Routing Policies available for this flow
                  </p>
                ))}
            </div>
            <FormItem gap={false} label="Filter by status">
              <Select
                options={CREATE_DATASET_DECISION_CODES}
                placeholder="Select status"
                selectAllOption={{
                  value: "All statuses",
                }}
                value={statusCodes}
                multiple
                showSelectAllAsOption
                onChange={setStatusCodes}
              />
            </FormItem>
            <Accordion>
              <Accordion.Item
                dataLoc="advanced-settings-accordion"
                label="Advanced settings"
                value="advancedSettings"
              >
                <div className="mb-4 flex flex-col gap-y-5">
                  {subSamplingSettings}
                  {limitMockDataSettings}
                  {purpose === "test_run" && limitExpectedOutputsSettings}
                  {purpose === "test_run" && !isIcebergDatasetGenEnabled && (
                    <OutcomesFilter
                      value={outcomesFilter}
                      onChange={setOutcomesFilter}
                    />
                  )}
                </div>
              </Accordion.Item>
            </Accordion>
          </div>
          <div className="min-w-0 flex-1 pl-4">
            {(() => {
              if (!queryIsEnabled) {
                return <EmptyState type="no_version_selected" />;
              }

              if (!result.data) {
                return result.error ? (
                  <EmptyState
                    type={
                      result.error instanceof AxiosError &&
                      result.error.response?.status === 408
                        ? "timeout"
                        : "unknown_error"
                    }
                  />
                ) : (
                  <TableSkeleton />
                );
              }

              return result.data.total_count === 0 ? (
                <EmptyState type="no_rows_matching" />
              ) : (
                <AssembleDataTable
                  columns={TableColumns({
                    requestColumnNames: result.data.request_column_names,
                  })}
                  data={result.data.preview}
                />
              );
            })()}
          </div>
        </div>
      </Modal.Content>
      <Modal.Footer
        primaryButton={
          <Tooltip
            disabled={!(!submitAvailable && !name && downloadAvailable)}
            placement="top"
            title="The dataset must have a name"
            asChild
          >
            <div>
              {wrapWithSubSamplingTooltip(
                <Button
                  dataLoc="assemble-dataset-save-button"
                  disabled={!submitAvailable}
                  loading={createDatasetMutation.isPending}
                  variant="primary"
                  onClick={handleOnSubmit}
                >
                  Save dataset
                </Button>,
              )}
            </div>
          </Tooltip>
        }
      >
        {wrapWithSubSamplingTooltip(
          <DownloadButton
            disabled={!downloadAvailable}
            downloadDetails={
              downloadAvailable
                ? getDownloadDetails(
                    {
                      flowSlug: flow.slug,
                      flowVersionIds: selectedVersions,
                      statusCodes,
                      subSamplingSize: enableRandomSampling
                        ? sampleSize
                        : undefined,
                      outcomeFilters: outcomesFilter
                        ? [outcomesFilter]
                        : undefined,
                      timeWindow,
                    },
                    dateRangeMode,
                    selectedTrafficPolicy,
                  )
                : undefined
            }
          />,
        )}
      </Modal.Footer>
    </>
  );
};

const getDownloadDetails = (
  base: DownloadDetails,
  dateRangeMode: DateRangeMode,
  selectedTrafficPolicy: TrafficPolicyInDB | undefined,
): DownloadDetails => {
  return dateRangeMode === "time_window" || !selectedTrafficPolicy
    ? base
    : {
        ...base,
        trafficPolicyId: selectedTrafficPolicy.id,
        timeWindow: getTimeWindowForTrafficPolicy(selectedTrafficPolicy),
      };
};

const VersionSelectValue: React.FC<{ version: FlowVersion }> = ({
  version,
}) => (
  <div className="flex justify-between">
    {version.name}{" "}
    {version.status === FlowVersionStatus.ARCHIVED && (
      <Pill variant="gray">
        <Pill.Text>Archived</Pill.Text>
      </Pill>
    )}
  </div>
);

const VersionsSelect: React.FC<{
  versions: FlowVersion[];
  value: string[];
  onChange: React.Dispatch<React.SetStateAction<string[]>>;
}> = ({ versions, value, onChange }) => {
  const firstArchivedVersionIndex = versions.findIndex(
    (version) => version.status === FlowVersionStatus.ARCHIVED,
  );
  const options = versions.flatMap((version, index) => {
    const option = {
      key: version.id,
      value: <VersionSelectValue version={version} />,
    };

    if (firstArchivedVersionIndex === index) {
      return [SELECT_DIVIDER, option];
    }
    return [option];
  });
  return (
    <Select
      dataLoc="assemble-dataset-version-select"
      options={options}
      placeholder="Select Decision Flow version"
      selectAllOption={{
        value: "All versions",
      }}
      value={value}
      multiple
      showSelectAllAsOption
      onChange={onChange}
    />
  );
};
